import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BRAND } from '../theme';
import { explorerUrl } from '../_shared/wallet-app/chains';
import { isStuckState, stateLabel } from '../lib/bridgeApi';

// StateTimeline
//
// Compact vertical timeline of bridge events. Each event = a chip + a
// timestamp + a tx hash that links to the explorer. Pending steps render
// as pulsing skeletons. A stuck_* state renders red with the rationale and
// a "Contact ops" button.
//
// Events come in two shapes from the backend (we accept both):
//
//   { ts, state, txHash, chainId }            (from /bridge/status events[])
//   { ts, label, txHash, chainId }            (legacy)
//
// Synthetic events are also derived from the top-level status fields:
//   burn_submitted, burn_confirmed, relay_submitted, mint_confirmed
//
// Props:
//   status         The /bridge/status payload
//   pending        Array of pending phase labels to render as skeletons
//   onContactOps   Callback for the Contact ops CTA on stuck states

const STATE_COLOR = {
  intent_created: 'default',
  burn_submitted: 'info',
  burn_confirmed: 'primary',
  relay_submitted: 'info',
  mint_confirmed: 'success',
};

export default function StateTimeline({ status, pending = [], onContactOps }) {
  if (!status) {
    return (
      <Stack spacing={1.25} data-testid="state-timeline-loading">
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={48} />
      </Stack>
    );
  }

  const events = deriveEvents(status);
  const stuck = isStuckState(status.state);

  return (
    <Stack
      spacing={0}
      sx={{ position: 'relative', pl: 3.5 }}
      data-testid="state-timeline"
    >
      {/* Vertical guide-line */}
      <Box
        sx={{
          position: 'absolute',
          left: 12,
          top: 8,
          bottom: 8,
          width: 2,
          background: `linear-gradient(180deg, ${BRAND.violet}, ${BRAND.cyan})`,
          opacity: 0.4,
          borderRadius: 1,
        }}
      />

      {events.map((ev, i) => (
        <TimelineRow key={`ev-${i}-${ev.state}`} event={ev} />
      ))}

      {pending.map((label) => (
        <TimelineRow
          key={`pending-${label}`}
          event={{ state: label, label, pending: true }}
        />
      ))}

      {stuck && (
        <Alert
          severity="warning"
          sx={{ mt: 2, ml: -3.5 }}
          action={
            <Button color="inherit" size="small" onClick={onContactOps}>
              Contact ops
            </Button>
          }
          data-testid="stuck-alert"
        >
          Bridge is stuck at "{stateLabel(status.state)}". An operator will
          reconcile it shortly; if not within the SLA, the burn refunds
          automatically.
        </Alert>
      )}
    </Stack>
  );
}

function TimelineRow({ event }) {
  const { state, txHash, chainId, ts, label, pending } = event;

  // Dot.
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 1, position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          left: -22,
          top: 16,
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: pending ? BRAND.bg2 : BRAND.violet,
          border: `2px solid ${pending ? BRAND.border : BRAND.violetLight}`,
          animation: pending ? 'rowPulse 1.4s ease-in-out infinite' : 'none',
          '@keyframes rowPulse': {
            '0%, 100%': { opacity: 0.4 },
            '50%':      { opacity: 1 },
          },
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: pending ? 0 : 0.5 }}>
          <Chip
            label={label || stateLabel(state)}
            size="small"
            color={pending ? 'default' : (STATE_COLOR[state] || 'default')}
            variant={pending ? 'outlined' : 'filled'}
            sx={{ fontWeight: 600 }}
            data-testid={`timeline-chip-${state}`}
          />
          {!pending && ts && (
            <Typography variant="caption" sx={{ color: BRAND.textSecondary }}>
              {new Date(ts * 1000).toLocaleString()}
            </Typography>
          )}
          {pending && (
            <Typography variant="caption" sx={{ color: BRAND.textSecondary }}>
              waiting…
            </Typography>
          )}
        </Stack>
        {!pending && txHash && (
          <TxHashLine txHash={txHash} chainId={chainId} />
        )}
      </Box>
    </Box>
  );
}

function TxHashLine({ txHash, chainId }) {
  const url = explorerUrl(chainId, txHash);
  const short = `${txHash.slice(0, 10)}…${txHash.slice(-8)}`;
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', color: BRAND.textSecondary }}
      >
        {short}
      </Typography>
      <Tooltip title="Copy">
        <Box
          component="button"
          onClick={() => navigator.clipboard?.writeText(txHash)}
          sx={{
            border: 0, background: 'none', cursor: 'pointer',
            color: BRAND.textSecondary, p: 0.25, display: 'inline-flex',
            '&:hover': { color: BRAND.violetLight },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 12 }} />
        </Box>
      </Tooltip>
      {url && (
        <Tooltip title="Open in block explorer">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ color: BRAND.textSecondary, display: 'inline-flex' }}
            onMouseOver={(e) => (e.currentTarget.style.color = BRAND.cyan)}
            onMouseOut={(e) => (e.currentTarget.style.color = BRAND.textSecondary)}
          >
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </a>
        </Tooltip>
      )}
    </Stack>
  );
}

// Compose synthetic events from the top-level status payload. We don't
// trust the server's events[] array to be complete — derive on the client
// so the UI is always coherent.
function deriveEvents(status) {
  const out = [];
  const push = (state, ts, txHash, chainId) => {
    if (!ts) return;
    out.push({ state, ts, txHash, chainId });
  };

  // intent_created — fallback to status.requestedAt or status.ts.
  push('intent_created', status.intentCreatedAt || status.requestedAt, null, null);

  if (status.burnTxHash || status.burnConfirmedAt || status.burnSubmittedAt) {
    push('burn_submitted', status.burnSubmittedAt || status.burnConfirmedAt, status.burnTxHash, status.srcChainId);
  }
  if (status.burnConfirmedAt) {
    push('burn_confirmed', status.burnConfirmedAt, status.burnTxHash, status.srcChainId);
  }
  if (status.relayTxHash || status.relaySubmittedAt) {
    push('relay_submitted', status.relaySubmittedAt, status.relayTxHash, status.dstChainId);
  }
  if (status.mintConfirmedAt) {
    push('mint_confirmed', status.mintConfirmedAt, status.mintTxHash, status.dstChainId);
  }

  // Use server-supplied events for anything we missed (preserve original
  // order, dedupe by state).
  if (Array.isArray(status.events)) {
    const seen = new Set(out.map((e) => e.state));
    for (const ev of status.events) {
      if (!ev) continue;
      const st = ev.state || ev.kind;
      if (!st || seen.has(st)) continue;
      out.push({
        state: st,
        ts: ev.ts || ev.timestamp,
        txHash: ev.txHash || ev.tx,
        chainId: ev.chainId,
      });
      seen.add(st);
    }
  }

  return out;
}
